import { useState } from "react";
import Swal from 'sweetalert2';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Fade from '@mui/material/Fade';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CancelIcon from '@mui/icons-material/Cancel';

import CustomButton from "../custom_components/CustomButton";

const STATUSES = [
    {
        value: "Pending",
        label: "Mark as Pending",
        icon: <PendingOutlinedIcon fontSize="small" />,
        color: "#D97706",    // Warm amber
        destructive: false,
    },
    {
        value: "In_Transit",
        label: "Mark as In Transit",
        icon: <FlightTakeoffIcon fontSize="small" />,
        color: "#C2410C",    // Deep orange — movement, urgency
        destructive: false,
    },
    {
        value: "Arrived",
        label: "Mark as Arrived",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        color: "#15803D",    // Emerald green — success, delivered
        destructive: false,
    },
    {
        value: "Shipped",
        label: "Mark as Shipped",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        color: "#0369A1",    // Sky blue — in motion, informational
        destructive: false,
    },
    {
        value: "Cancelled",
        label: "Mark as Cancelled",
        icon: <CancelIcon fontSize="small" />,
        color: "#DC2626",    // Rose red — loss, strong negative
        destructive: true,
    },
    {
        value: "Forfeited",
        label: "Mark as Forfeited",
        icon: <BlockOutlinedIcon fontSize="small" />,
        color: "#BE123C",    // Rose red — loss, strong negative
        destructive: true,
    },
    {
        value: "Disposed",
        label: "Mark as Disposed",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "#374151",    // Cool slate — inactive, closed out
        destructive: true,
    },
];

export default function MenuOrderStatus({ orders, onUpdate, disabled }) {

    const apiUrl = process.env.REACT_APP_API_URL;
    const [anchorEl, setAnchorEl] = useState(null);

    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [isLoading, setIsLoading] = useState(false);

    // SET ORDER STATUS
    const handleSetOrderStatus = async (valStat) => {
        setIsLoading(true);
        handleClose();

        try {
            const response = await fetch(`${apiUrl}manage_orders/update_order_status.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orders: orders,
                    valueStatus: valStat
                })
            });

            const result = await response.json();

            if (result.status === "success") {

                setIsLoading(false);

                onUpdate({
                    status: valStat,
                    orders: result.orders || [],
                });

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Error getting orders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = async (status) => {

        handleClose();

        if (status.destructive) {

            const result = await Swal.fire({
                title: `Mark as ${status.value.toLowerCase()}?`,
                html: `
                This action cannot be undone.<br/><br/>
                The order will be marked as
                <strong>${status.value.toLowerCase()}</strong>.
            `,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                confirmButtonColor: "#DC2626",
            });

            if (result.isConfirmed) {
                handleSetOrderStatus(status.value);
            }

            return;
        }

        handleSetOrderStatus(status.value);
    };

    const firstDestructiveIndex = STATUSES.findIndex((s) => s.destructive);

    return (
        <>
            <CustomButton
                label="More"
                disabled={disabled}
                loading={isLoading}
                startIcon={<MoreVertIcon fontSize="small" />}
                onClick={handleClick}
            />

            <Menu
                aria-hidden="false"
                id="order-status-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slots={{ transition: Fade }}
                slotProps={{
                    list: {
                        "aria-labelledby": "order-menu-button",
                    },
                }}
                transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                }}
                anchorOrigin={{
                    horizontal: "right",
                    vertical: "bottom",
                }}
            >
                {STATUSES.map((status, index) => [
                    index === firstDestructiveIndex && (
                        <Divider
                            key={`divider-${status.value}`}
                            sx={{ my: 0.5 }}
                        />
                    ),

                    <MenuItem
                        key={status.value}
                        onClick={() => handleSelect(status)}
                        sx={{
                            color: status.destructive
                                ? status.color
                                : "text.primary",

                            "&:hover": {
                                backgroundColor: status.destructive
                                    ? `${status.color}12`
                                    : undefined,
                            },
                        }}
                    >
                        <ListItemIcon sx={{ color: status.color }}>
                            {status.icon}
                        </ListItemIcon>

                        <ListItemText>
                            {status.label}
                        </ListItemText>
                    </MenuItem>
                ])}
            </Menu>
        </>
    );
}